// src/pages/Pricing.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import API_URL from '../config';
import './Pricing.css';

function Pricing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showContactInfo, setShowContactInfo] = useState(false);

  const handleContactUs = () => {
    setShowContactInfo(true);
  };

  const handleGetStarted = () => {
    if (!token) {
      navigate('/signup');
    } else {
      navigate('/group/new');
    }
  };

  const handleUpgrade = async (plan) => {
    // Om användaren inte är inloggad, skicka till signup
    if (!token) {
      navigate('/signup');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let priceId;
      
      // Bestäm vilket Price ID baserat på plan
      if (plan === 'plus') {
        priceId = process.env.REACT_APP_STRIPE_PLUS_PRICE_ID;
      } else if (plan === 'premium') {
        priceId = process.env.REACT_APP_STRIPE_PREMIUM_PRICE_ID;
      } else if (plan === 'pro') {
        priceId = process.env.REACT_APP_STRIPE_PRO_PRICE_ID;
      } else {
        throw new Error('Ogiltig plan');
      }

      if (!priceId) {
        throw new Error('Price ID saknas i environment variables');
      }

      // Anropa backend för att skapa Stripe Checkout Session
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priceId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Kunde inte skapa betalningssession');
      }

      // Redirect till Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      console.error('Upgrade error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <div className="pricing-page">
        <div className="pricing-header">
          <h1>{t('pricing.title')}</h1>
          <p>{t('pricing.subtitle')}</p>
        </div>

        {error && (
          <div className="error-message" style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '2rem',
            maxWidth: '600px',
            margin: '0 auto 2rem',
            textAlign: 'center'
          }}>
            ⚠️ {error}
          </div>
        )}

        <div className="pricing-container">
          {/* Gratisplan */}
          <div className="pricing-card">
            <div className="plan-header">
              <h2>{t('pricing.plans.free.name')}</h2>
              <div className="price">
                <span className="amount">{t('pricing.plans.free.price')}</span>
                <span className="period">{t('pricing.perMonth')}</span>
              </div>
            </div>

            <div className="plan-features">
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.free.features.groups')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.free.features.participants')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.free.features.invitations')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.free.features.reminders')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.free.features.statistics')}</span>
              </div>
            </div>

            <button 
              className="plan-button secondary"
              onClick={handleGetStarted}
            >
              {t('pricing.plans.free.button')}
            </button>
          </div>

          {/* Plus - NYT */}
          <div className="pricing-card">
            <div className="plan-header">
              <h2>{t('pricing.plans.plus.name')}</h2>
              <div className="price">
                <span className="amount">{t('pricing.plans.plus.price')}</span>
                <span className="period">{t('pricing.perMonth')}</span>
              </div>
            </div>

            <div className="plan-features">
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.plus.features.groups')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.plus.features.participants')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.plus.features.upload')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.plus.features.invitations')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.plus.features.reminders')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.plus.features.statistics')}</span>
              </div>
            </div>

            <button 
              className="plan-button primary"
              onClick={() => handleUpgrade('plus')}
              disabled={loading}
            >
              {loading ? t('payment.processing', 'Bearbetar...') : t('pricing.plans.plus.button')}
            </button>
          </div>

          {/* Premium */}
          <div className="pricing-card featured">
            <div className="badge">{t('pricing.popularBadge')}</div>
            <div className="plan-header">
              <h2>{t('pricing.plans.premium.name')}</h2>
              <div className="price">
                <span className="amount">{t('pricing.plans.premium.price')}</span>
                <span className="period">{t('pricing.perMonth')}</span>
              </div>
            </div>

            <div className="plan-features">
              <div className="feature">
                <span className="check">✓</span>
                <span dangerouslySetInnerHTML={{ __html: t('pricing.plans.premium.features.groups') }} />
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.participants')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.upload')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.keyPlayers')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.invitations')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.reminders')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.statistics')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.premium.features.support')}</span>
              </div>
            </div>

            <button 
              className="plan-button primary"
              onClick={() => handleUpgrade('premium')}
              disabled={loading}
            >
              {loading ? t('payment.processing', 'Bearbetar...') : t('pricing.plans.premium.button')}
            </button>
          </div>

          {/* Pro */}
          <div className="pricing-card">
            <div className="plan-header">
              <h2>{t('pricing.plans.pro.name')}</h2>
              <div className="price">
                <span className="amount">{t('pricing.plans.pro.price')}</span>
                <span className="period">{t('pricing.perMonth')}</span>
              </div>
            </div>

            <div className="plan-features">
              <div className="feature">
                <span className="check">✓</span>
                <span dangerouslySetInnerHTML={{ __html: t('pricing.plans.pro.features.groups') }} />
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.participants')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.upload')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.keyPlayers')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.invitations')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.reminders')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.statistics')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.support')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.api')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.pro.features.sla')}</span>
              </div>
            </div>

            <button 
              className="plan-button primary"
              onClick={() => handleUpgrade('pro')}
              disabled={loading}
            >
              {loading ? t('payment.processing', 'Bearbetar...') : t('pricing.plans.pro.button')}
            </button>
          </div>

          {/* Enterprise */}
          <div className="pricing-card enterprise">
            <div className="plan-header">
              <h2>{t('pricing.plans.enterprise.name')}</h2>
              <div className="price">
                <span className="amount" style={{ fontSize: '32px' }}>
                  {t('pricing.plans.enterprise.price')}
                </span>
              </div>
            </div>

            <div className="plan-features">
              <div className="feature">
                <span className="check">✓</span>
                <span dangerouslySetInnerHTML={{ __html: t('pricing.plans.enterprise.features.groups') }} />
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.enterprise.features.participants')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.enterprise.features.everything')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.enterprise.features.manager')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.enterprise.features.sla')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.enterprise.features.integration')}</span>
              </div>
              <div className="feature">
                <span className="check">✓</span>
                <span>{t('pricing.plans.enterprise.features.whiteLabel')}</span>
              </div>
            </div>

            <button 
              className="plan-button enterprise-btn"
              onClick={handleContactUs}
            >
              {t('pricing.plans.enterprise.button')}
            </button>

            {showContactInfo && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                textAlign: 'center'
              }}>
                <p style={{ margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
                  AB Blackmarx
                </p>
                <p style={{ margin: '0', fontSize: '16px', color: '#667eea' }}>
                  info@blackmarx.se
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Jämförelsetabell */}
        <div className="comparison-table">
          <h2>{t('pricing.comparison.title')}</h2>
          <table>
            <thead>
              <tr>
                <th>{t('pricing.comparison.feature')}</th>
                <th>{t('pricing.plans.free.name')}</th>
                <th>{t('pricing.plans.plus.name')}</th>
                <th>{t('pricing.plans.premium.name')}</th>
                <th>{t('pricing.plans.pro.name')}</th>
                <th>{t('pricing.plans.enterprise.name')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('pricing.comparison.numberOfGroups')}</td>
                <td>2</td>
                <td>5</td>
                <td>20</td>
                <td>100</td>
                <td>{t('pricing.comparison.unlimited')}</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.participantsPerGroup')}</td>
                <td>15</td>
                <td>50</td>
                <td>200</td>
                <td>{t('pricing.comparison.unlimited')}</td>
                <td>{t('pricing.comparison.unlimited')}</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.automaticInvitations')}</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.emailReminders')}</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.uploadMemberLists')}</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.keyPlayers')}</td>
                <td>—</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.statisticsHistory')}</td>
                <td>{t('pricing.comparison.basic')}</td>
                <td>{t('pricing.comparison.basic')}</td>
                <td>{t('pricing.comparison.advanced')}</td>
                <td>{t('pricing.comparison.advanced')}</td>
                <td>{t('pricing.comparison.advanced')}</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.apiAccess')}</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.support')}</td>
                <td>{t('pricing.comparison.standard')}</td>
                <td>{t('pricing.comparison.standard')}</td>
                <td>{t('pricing.comparison.priority')}</td>
                <td>{t('pricing.comparison.priority')}</td>
                <td>{t('pricing.comparison.dedicated')}</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.slaGuarantee')}</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td>✓</td>
                <td>✓</td>
              </tr>
              <tr>
                <td>{t('pricing.comparison.whiteLabel')}</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td>✓</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* FAQ */}
        <div className="faq-section">
          <h2>{t('pricing.faq.title')}</h2>
          <div className="faq-item">
            <h3>{t('pricing.faq.q1')}</h3>
            <p>{t('pricing.faq.a1')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faq.q2')}</h3>
            <p>{t('pricing.faq.a2')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faq.q3')}</h3>
            <p>{t('pricing.faq.a3')}</p>
          </div>
          <div className="faq-item">
            <h3>{t('pricing.faq.q4')}</h3>
            <p>{t('pricing.faq.a4')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
